/*
 * Kardima metal filter. Spec: docs/collection-spec.md § "Metal filter (top of page)".
 *
 * Swaps every product card's image to the chosen metal. It does NOT change which products are
 * shown — every product in the catalogue exists in both metals, so hiding cards would only ever
 * remove a product for no reason. A card with no image for the chosen metal keeps the one it
 * has and is marked data-metal-missing.
 *
 * Only src/srcset/alt are rewritten. The alternate metal is never fetched until it is asked
 * for, which is why the markup carries data attributes rather than a second hidden <img>.
 */
class KardimaMetalFilter extends HTMLElement {
  connectedCallback() {
    this.chips = Array.from(this.querySelectorAll('[data-metal]'));
    if (!this.chips.length) return;

    this.chips.forEach((chip) => {
      chip.addEventListener('click', () => this.select(chip.dataset.metal, true));
    });

    // A shared or reloaded URL keeps its metal. Falls back to the markup's default.
    const fromUrl = new URLSearchParams(window.location.search).get('metall');
    const initial = this.chips.some((c) => c.dataset.metal === fromUrl)
      ? fromUrl
      : this.dataset.defaultMetal;

    // The server already rendered the default metal, so only re-paint if it differs.
    this.select(initial, false, initial === this.dataset.defaultMetal);
  }

  select(metal, pushToUrl, skipImages) {
    this.chips.forEach((chip) => {
      chip.setAttribute('aria-pressed', String(chip.dataset.metal === metal));
    });

    if (!skipImages) this.swapImages(metal);
    this.markSwatches(metal);

    if (!pushToUrl) return;

    const url = new URL(window.location.href);
    if (metal === this.dataset.defaultMetal) {
      url.searchParams.delete('metall');
    } else {
      url.searchParams.set('metall', metal);
    }
    window.history.replaceState({}, '', url);
  }

  swapImages(metal) {
    // "gult-gull" -> gold, "hvitt-gull" -> white. Keys match the data-metal-*-src attributes.
    const key = metal === 'hvitt-gull' ? 'white' : 'gold';

    document.querySelectorAll('[data-metal-swap]').forEach((img) => {
      const src = img.dataset[`metal${key === 'white' ? 'White' : 'Gold'}Src`];
      const srcset = img.dataset[`metal${key === 'white' ? 'White' : 'Gold'}Srcset`];
      const alt = img.dataset[`metal${key === 'white' ? 'White' : 'Gold'}Alt`];

      if (!src) {
        // No image for this metal — leave the card as-is rather than blanking it.
        img.setAttribute('data-metal-missing', metal);
        return;
      }

      img.removeAttribute('data-metal-missing');
      // srcset first: setting src alone would briefly show the old srcset candidate.
      if (srcset) img.srcset = srcset;
      img.src = src;
      if (alt) img.alt = alt;
    });
  }

  markSwatches(metal) {
    document.querySelectorAll('.card-metals').forEach((list) => {
      list.querySelectorAll('[data-metal-swatch]').forEach((dot) => {
        dot.classList.toggle('card-metals__dot--active', dot.dataset.metalSwatch === metal);
      });
    });
  }
}

if (!customElements.get('kardima-metal-filter')) {
  customElements.define('kardima-metal-filter', KardimaMetalFilter);
}
